/**
 * PostHog event names and channel identifiers for the Campaign Builder.
 * Centralized here so any future rename touches one place instead of drifting
 * across every capture call. Mirrors the analytics spec (PAD-32157).
 *
 * The `channel` property is attached automatically to every event of the
 * session via posthog.register({ channel }) at the builder entry point,
 * so individual capture calls do not need to pass it.
 */

export const CB_EVENTS = {
  AUDIENCE_REACHED: "CB - Audience Reached",
  DESIGN_REACHED: "CB - Design Reached",
  SUBMIT_REACHED: "CB - Submit Reached",
  PAYMENT_CONFIRMED: "CB - Payment Confirmed",
  PAYMENT_FAILED: "CB - Payment Failed",
} as const;

export type CBEventName = (typeof CB_EVENTS)[keyof typeof CB_EVENTS];

/**
 * Channel codes used as the `channel` session property. Match the
 * BuilderChannel type in campaign-builder/config/channelRegistry.ts so a
 * consumer resolving the channel from the loaded campaign can register
 * it directly without a mapping table.
 */
export const CB_CHANNELS = {
  ADM: "ADM",
  EDDM: "EDDM",
  H2H: "H2H",
  D2D: "D2D",
} as const;

export type CBChannel = (typeof CB_CHANNELS)[keyof typeof CB_CHANNELS];

/** Payment failure reason token — categorizes the different failure paths. */
export const CB_PAYMENT_FAILURE = {
  CARD_DECLINED: "card_declined",
  UNKNOWN: "unknown",
} as const;
export type CBPaymentFailureReason = (typeof CB_PAYMENT_FAILURE)[keyof typeof CB_PAYMENT_FAILURE];

/** Which payment method was used for a confirmed payment. */
export const CB_PAYMENT_METHOD = {
  SAVED_CARD: "saved_card",
  NEW_CARD: "new_card",
} as const;
export type CBPaymentMethod = (typeof CB_PAYMENT_METHOD)[keyof typeof CB_PAYMENT_METHOD];

/**
 * Copilot control-mode telemetry (WP-H3 of the Copilot control-mode plan).
 * Maps the library's transport-agnostic `ControlEventName` (see
 * `@oppizi-ecosystem/copilot`'s `control/events.ts`) to PostHog event names.
 * Named 1:1 with the events the hook can emit today; `reconnected` has no
 * dedicated event here (folded back into CONNECTION_LOST's counterpart —
 * i.e. not tracked as its own event) since the spec below does not define one.
 */
export const COPILOT_CONTROL_EVENTS = {
  REQUESTED: "CB - Copilot Control Requested",
  GRANTED: "CB - Copilot Control Granted",
  DECLINED: "CB - Copilot Control Declined",
  STOPPED: "CB - Copilot Control Stopped",
  PAUSED: "CB - Copilot Control Paused",
  RESUMED: "CB - Copilot Control Resumed",
  ACTION: "CB - Copilot Control Action",
  REFUSED: "CB - Copilot Control Refused",
  TIMEOUT: "CB - Copilot Control Timeout",
  NO_EFFECT: "CB - Copilot Control No Effect",
  ERROR: "CB - Copilot Control Error",
  CONNECTION_LOST: "CB - Copilot Control Connection Lost",
  TAKEOVER: "CB - Copilot Control Takeover",
} as const;

/**
 * Copilot panel lifecycle (Ag11 "widget closes itself" diagnosis). Every
 * close the library reports and every full page load while the panel is open
 * lands here, so the cause of an unexpected close can be named from analytics.
 * Captured by `src/copilot/panelTelemetry.mjs`.
 */
export const COPILOT_PANEL_EVENTS = {
  OPENED: "CB - Copilot Panel Opened",
  CLOSED: "CB - Copilot Panel Closed",
  UNLOADED_WHILE_OPEN: "CB - Copilot Panel Unloaded While Open",
} as const;

export type CopilotPanelEventName = (typeof COPILOT_PANEL_EVENTS)[keyof typeof COPILOT_PANEL_EVENTS];

/** Properties on a Copilot panel event — ids, reasons, paths and booleans only. */
export interface CopilotPanelEventProps {
  host: "builder";
  path: string;
  /** Library `CopilotOpenChangeReason` — opened/closed events only. */
  reason?: string;
  conversation_id?: string;
  /** Time since the last open — closed event only. */
  open_for_ms?: number;
  /** `document.visibilityState` at close time — closed event only. */
  visibility?: string;
  /** Unload event only; null when the destination is unknown. */
  next_url_same_origin?: boolean | null;
  /** Unload event only; `PageTransitionEvent.persisted` (bfcache). */
  persisted?: boolean;
}

export type CopilotControlEventName = (typeof COPILOT_CONTROL_EVENTS)[keyof typeof COPILOT_CONTROL_EVENTS];

/**
 * Properties allowed on a Copilot control-mode PostHog event. This is the
 * privacy boundary: NEVER add typed values, input labels, narration text, or
 * DOM snapshots here — those can carry PII/payment data through the control
 * channel. `src/copilot/controlTelemetry.js` strips anything not in this
 * shape before calling `posthog.capture`.
 */
export interface CopilotControlEventProps {
  conversation_id: string;
  campaign_id?: string | number;
  host: "builder";
  grant_scope?: "conversation" | "once";
  action?: string;
  cy?: string;
  status?: string;
  step?: string;
  actions_used?: number;
  duration_ms?: number;
  reason?: string;
  path: string;
}
